'use client';
import React, { useState, useEffect } from 'react';
import { IDataFrameField, IDataFrameSchema, codeLanguageOptions } from '@/lib/dataFrameTypes';
import { ColumnWithDType } from '../../../types';
import { getColumnRenderMethod } from '../../lib';
import * as CellDetailsRenderer from '../../../FieldRenderers/CellDetailsRenderer';
import * as Styled from './styled';
import { Row } from '@/ui/primatives';
import Input from '@/components/Input';
const getColumnType = (schema: IDataFrameSchema, columnKey: string) => {
  return schema.fields.find((field) => field.name === columnKey)?.dtype;
};

const getCellValue = (row: any, key: any) => {
  // TODO: figure out if ?.value EVER has relevant meaning - it does not seem to.
  if (row[key]?.value === false) {
    return false;
  }
  // return row[key]?.value || row[key];
  return row[key];
};

const CellDetailsRenderComponents: any = {
  diff_status: CellDetailsRenderer.DiffStatus,
  image: CellDetailsRenderer.Image,
  link: CellDetailsRenderer.Link,
  code: CellDetailsRenderer.Code,
  markdown: CellDetailsRenderer.Markdown,
  list: CellDetailsRenderer.List,
  struct: CellDetailsRenderer.Struct,
  bool: CellDetailsRenderer.Boolean,
  float: CellDetailsRenderer.Number,
  int: CellDetailsRenderer.Number,
  default: CellDetailsRenderer.String,
};

const CellContent: React.FC<{
  schema: IDataFrameSchema;
  columnKey: string;
  language?: string;
  editable: boolean;
  onChange?: (value: any) => void;
  row: any;
  fileRootPath: string;
}> = ({ schema, columnKey, language, editable, onChange, row, fileRootPath }) => {
  const field = schema.fields.find((field) => field.name === columnKey) as ColumnWithDType;
  const renderMethod = getColumnRenderMethod(field as IDataFrameField);
  const cell = getCellValue(row, columnKey);
  const valueDefined = cell && typeof cell === 'object' && 'match' in cell; //typeof props.row[props.column.name as any].value !== undefined;
  const value = valueDefined ? cell.value : cell;

  const CellDetailsRenderComponent = CellDetailsRenderComponents[renderMethod] || CellDetailsRenderComponents.default;

  const update = (value: any) => {
    const workingRow = { ...row };
    workingRow[columnKey] = value;
    onChange?.(workingRow);
  };

  return (
    <CellDetailsRenderComponent
      field={field}
      value={value}
      rootPath={fileRootPath}
      onChange={editable && update}
      language={language}
    />
  );
};

const CodeLanguagePicker: React.FC<{
  language: string;
  onChange: (language: string) => void;
}> = ({ language, onChange }) => {
  return (
    <Input.SelectControl
      name="code-language"
      options={codeLanguageOptions}
      selectedValue={language}
      onChange={(value) => onChange(value)}
    />
  );
};

const CellDetails: React.FC<{
  schema: IDataFrameSchema;
  fileRootPath: string;
  row: any;
  onChange?: (value: any) => void;
  onClick: (value: any) => void;
  editable: boolean;
  columnKey: string;
  selected: boolean;
}> = ({ schema, fileRootPath, row, onChange, editable, columnKey, selected, onClick }) => {
  const columnType = getColumnType(schema, columnKey);
  const field = schema.fields.find((field) => field.name === columnKey);

  const schemaCodeLanguage = field?.metadata?._oxen?.render.language;
  const [language, setLanguage] = useState(schemaCodeLanguage || 'text');
  const metaData = field?.metadata;

  useEffect(() => {
    setLanguage(schemaCodeLanguage || 'text');
  }, [schemaCodeLanguage]);

  // console.log('CellDetails > row  ', row);

  const renderFunction = metaData?._oxen?.render;
  const isImage = renderFunction && renderFunction.func === 'image';
  const isCode = renderFunction && renderFunction.func === 'code';
  const isMarkdown = renderFunction && renderFunction.func === 'markdown';
  return (
    <Styled.Column $selected={selected} onClick={() => onClick({ row, columnKey })}>
      <Styled.ColumnHeading>
        <b>{columnKey}</b>
        <Row alignItems="center" gap={5}>
          <b>({renderFunction?.func || columnType})</b>
          {isCode && <CodeLanguagePicker language={language} onChange={setLanguage} />}
        </Row>
      </Styled.ColumnHeading>
      <Styled.ColumnBody
        $selected={selected}
        $isImage={isImage}
        $isCode={isCode}
        $isMarkdown={isMarkdown}
        $editable={!!(onChange && editable)}
      >
        <CellContent
          schema={schema}
          columnKey={columnKey}
          language={language}
          row={row}
          editable={editable}
          onChange={onChange}
          fileRootPath={fileRootPath}
        />
      </Styled.ColumnBody>
    </Styled.Column>
  );
};

export default CellDetails;
